import { AsyncPipe, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { DummyFollowButtonComponent } from '@shared/shared/components/dummy-follow-button/dummy-follow-button.component';
import { OnboardingDialogClose } from '@shared/shared/enums/onboarding-dialog-close';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { getAllCustomAssets } from '@shared/store/customizable-assets/selectors';
import * as UserStore from '@shared/store/user';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

export interface Slide {
  imageId: string;
  titleKey: string;
  descriptionKey: string;
  moduleName: string;
}
@Component({
  selector: 'ten-onboarding-popup',
  templateUrl: './onboarding-popup.component.html',
  styleUrls: ['./onboarding-popup.component.scss'],
  standalone: true,
  imports: [NgIf, AsyncPipe, DummyFollowButtonComponent, MatButtonModule, MatIconModule, TranslateModule],
})
export class OnboardingPopupComponent implements OnDestroy, OnInit {
  public customVariables$ = this.customVariablesService.variables;
  public currentStep = 1;
  public stepsArray: Array<Slide> = [];
  public customAssets$ = this.ngrxStore.select(getAllCustomAssets);

  @ViewChild('nextButton', { static: false }) public nextButton: MatButton;
  @ViewChild('endButton', { static: false }) public endButton: MatButton;

  private unsubscribe$ = new Subject<void>();

  public user_authenticated$: Observable<boolean> = this.ngrxStore.select(UserStore.getUserAuthenticated);

  constructor(
    public dialogRef: MatDialogRef<OnboardingPopupComponent>,
    private customVariablesService: CustomisableVariablesService,
    private siteSettingsService: SiteSettingsService,
    private angulartics2Segment: Angulartics2Segment,
    private ngrxStore: Store<UserStore.UserState>
  ) {}

  private slideImages: { [id: string]: Slide } = {
    landing: {
      imageId: 'onboarding_tutorial_landing_slide',
      titleKey: 'slider_landing_title',
      descriptionKey: 'slider_landing_description',
      moduleName: 'landing',
    },
    travel: {
      imageId: 'onboarding_tutorial_travel_slide',
      titleKey: 'slider_travel_title',
      descriptionKey: 'slider_travel_description',
      moduleName: 'travel',
    },
    dining: {
      imageId: 'onboarding_tutorial_dining_slide',
      titleKey: 'slider_dining_title',
      descriptionKey: 'slider_dining_description',
      moduleName: 'dining',
    },
    entertainment: {
      imageId: 'onboarding_tutorial_entertainment_slide',
      titleKey: 'slider_etertainment_title', // misspelled in django
      descriptionKey: 'slider_etertainment_description', // misspelled in django
      moduleName: 'entertainment',
    },
    preferences: {
      imageId: 'onboarding_tutorial_artist_slide',
      titleKey: 'slider_follow_artist_title',
      descriptionKey: 'slider_follow_artist_description',
      moduleName: 'preferences',
    },
    member_events: {
      imageId: 'onboarding_tutorial_event_slide',
      titleKey: 'slider_member_events_title',
      descriptionKey: 'slider_member_events_description',
      moduleName: 'member_events',
    },
    offers: {
      imageId: 'onboarding_tutorial_benefits_slide',
      titleKey: 'slider_benefits_title',
      descriptionKey: 'slider_benefits_description',
      moduleName: 'offers',
    },
    partners: {
      imageId: 'onboarding_tutorial_partners_slide',
      titleKey: 'slider_partners_title',
      descriptionKey: 'slider_partners_description',
      moduleName: 'partners',
    },
  };

  public ngOnInit() {
    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter((siteSettings: ISiteSettings) => !!siteSettings),
        take(1)
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.stepsArray.push(this.slideImages['landing']);

        if (siteSettings.switches.travel_module) {
          this.stepsArray.push(this.slideImages['travel']);
        }
        if (siteSettings.switches.dining_module) {
          this.stepsArray.push(this.slideImages['dining']);
        }
        if (siteSettings.switches.entertainment_module) {
          this.stepsArray.push(this.slideImages['entertainment']);
        }
        if (siteSettings.switches.preferences_artist) {
          this.stepsArray.push(this.slideImages['preferences']);
        }
        if (siteSettings.switches.member_events_module) {
          this.stepsArray.push(this.slideImages['member_events']);
        }
        if (siteSettings.switches.member_benefits_module) {
          this.stepsArray.push(this.slideImages['offers']);
        }
        if (siteSettings.switches.partners_module) {
          this.stepsArray.push(this.slideImages['partners']);
        }
      });
    // if the user logs out close the modal
    this.user_authenticated$.pipe(takeUntil(this.unsubscribe$)).subscribe((res) => {
      if (!res) {
        this.dialogRef.close();
      }
    });
  }

  /**
   * Changes the currently selected step either going forwards or backwards
   * or signals the end of the tour
   *
   * @param value
   */
  public changeStep(value: number) {
    if (value === 1 && this.currentStep < this.stepsArray.length) {
      this.currentStep += 1;

      if (this.currentStep === this.stepsArray.length) {
        this.endButton.focus();
      }
    } else if (value === 0 && this.currentStep > 1) {
      this.currentStep -= 1;

      if (this.currentStep === 1) {
        this.nextButton.focus();
      }
    }
  }

  /**
   * Either when the skip button or the end button is pressed
   * send final logs to segment and close the dialog
   */
  public finalStep() {
    if (this.currentStep < this.stepsArray.length) {
      this.angulartics2Segment.eventTrack('OnboardingSkipSlider', {
        timestamp: Date.now(),
        currentStep: this.currentStep,
        totalSteps: this.stepsArray.length,
        buttonType: OnboardingDialogClose.SKIP,
        moduleName: this.stepsArray[this.currentStep - 1].moduleName,
      });
      this.dialogRef.close(OnboardingDialogClose.SKIP);
    } else if (this.currentStep === this.stepsArray.length) {
      this.angulartics2Segment.eventTrack('OnboardingSkipSlider', {
        timestamp: Date.now(),
        currentStep: this.currentStep,
        totalSteps: this.stepsArray.length,
        buttonType: OnboardingDialogClose.END,
        moduleName: this.stepsArray[this.currentStep - 1].moduleName,
      });
      this.dialogRef.close(OnboardingDialogClose.END);
    }
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
